import { Alert } from "@mui/material";

import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { type MetadataChangeset } from "@/shared/domain/metadataChangeset";
import { userFacingErrorMessage } from "@/shared/http";

import { TagInput } from "./TagInput";
import { Tags } from "./Tags";
import { TagsGroup } from "./TagsGroup";

interface TagsChangesetEditorProps {
  tags: string[];
  /** Identifies the record being edited, so switching records resets the inputs. */
  recordKey: string;
  /** Sends one tag added or removed, as a metadata changeset. */
  onChangeset: (changeset: MetadataChangeset) => void;
  isPending: boolean;
  error: Error | null;
  onDismissError: () => void;
  readonly?: boolean;
}

/**
 * Edits the tags of any record whose update takes a metadata changeset: each
 * tag added or removed is one changeset.
 */
export function TagsChangesetEditor({
  tags,
  recordKey,
  onChangeset,
  isPending,
  error,
  onDismissError,
  readonly = false,
}: TagsChangesetEditorProps) {
  return (
    <>
      {readonly && tags.length === 0 && <>None</>}
      <TagsGroup style={{ marginTop: "var(--spacing-xs)" }}>
        <Tags
          key={recordKey}
          tags={tags}
          onDeleteTag={
            readonly ? undefined : (tag) => onChangeset({ remove_tags: [tag] })
          }
        />
        {!readonly && (
          <TagInput
            key={`${recordKey}-input`}
            autocompleteType={AutocompleteType.GenericTags}
            onAddTag={(tag) => onChangeset({ put_tags: [tag] })}
            tags={tags}
            isLoading={isPending}
          />
        )}
      </TagsGroup>

      {error && (
        <Alert
          onClose={onDismissError}
          severity="error"
          style={{ marginTop: "var(--spacing-xs)" }}
        >
          {userFacingErrorMessage(error)}
        </Alert>
      )}
    </>
  );
}
